// src/pages/business/BusinessPage.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Building2, Calendar, Pencil, PlusCircle, Save, Trash2 } from 'lucide-react';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { AppContext } from '../context/AppContext';

const FOUNDATION_TABS = [
  { key: 'goals', label: 'Goals' },
  { key: 'strategy', label: 'Strategy' },
  { key: 'policies', label: 'Policies' },
  { key: 'processes', label: 'Processes' },
  { key: 'procedures', label: 'Procedures' }
];

const EMPTY_DRAFT = {
  title: '',
  summary: '',
  details: '',
  status: 'draft',
  priority: 'medium',
  effectiveDate: ''
};

const createEmptySections = () => ({
  goals: [],
  strategy: [],
  policies: [],
  processes: [],
  procedures: []
});

const BusinessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, isDemoMode } = useAuth();
  const { activeBusiness } = useContext(AppContext);

  const [activeTab, setActiveTab] = useState('goals');
  const [sections, setSections] = useState(createEmptySections());
  const [selectedItemId, setSelectedItemId] = useState('');
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const isMainPage = location.pathname === '/business' || location.pathname === '/business/';

  useEffect(() => {
    const initialSections = createEmptySections();
    const source = activeBusiness?.foundationTabs || {};

    FOUNDATION_TABS.forEach((tab) => {
      const items = Array.isArray(source[tab.key]) ? source[tab.key] : [];
      initialSections[tab.key] = items;
    });

    setSections(initialSections);
    setActiveTab('goals');
    setSelectedItemId('');
    setDraft(EMPTY_DRAFT);
    setIsEditing(false);
    setStatusMessage('');
  }, [activeBusiness]);

  const currentTabItems = useMemo(() => {
    const items = sections[activeTab] || [];
    return [...items].sort((a, b) => {
      const aTime = a.updatedAt?.seconds ? a.updatedAt.seconds : (a.updatedAt ? new Date(a.updatedAt).getTime() : 0);
      const bTime = b.updatedAt?.seconds ? b.updatedAt.seconds : (b.updatedAt ? new Date(b.updatedAt).getTime() : 0);
      return bTime - aTime;
    });
  }, [sections, activeTab]);

  const selectedItem = useMemo(
    () => currentTabItems.find((item) => item.id === selectedItemId) || null,
    [currentTabItems, selectedItemId]
  );

  const formatDate = (value) => {
    if (!value) return 'N/A';
    try {
      if (value?.toDate) return value.toDate().toLocaleDateString();
      return new Date(value).toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };

  const persistSections = async (nextSections) => {
    if (!activeBusiness?.id) {
      setStatusMessage('Select a business first.');
      return false;
    }

    if (isDemoMode) {
      setStatusMessage('Demo mode: changes are local only and not persisted.');
      return true;
    }

    if (!currentUser?.uid) {
      setStatusMessage('You need to be signed in to update business content.');
      return false;
    }

    try {
      await updateDoc(doc(db, 'businesses', activeBusiness.id), {
        foundationTabs: nextSections,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      });
      return true;
    } catch (error) {
      console.error('Error saving business foundation tabs:', error);
      setStatusMessage('Could not save changes. Please try again.');
      return false;
    }
  };

  const onSelectTab = (tabKey) => {
    setActiveTab(tabKey);
    setSelectedItemId('');
    setIsEditing(false);
    setDraft(EMPTY_DRAFT);
    setStatusMessage('');
  };

  const onCreateNew = () => {
    setSelectedItemId('');
    setIsEditing(false);
    setDraft(EMPTY_DRAFT);
  };

  const onEditItem = (item) => {
    setSelectedItemId(item.id);
    setIsEditing(true);
    setDraft({
      title: item.title || '',
      summary: item.summary || '',
      details: item.details || '',
      status: item.status || 'draft',
      priority: item.priority || 'medium',
      effectiveDate: item.effectiveDate || ''
    });
  };

  const onSaveItem = async () => {
    if (!draft.title.trim()) {
      setStatusMessage('Title is required.');
      return;
    }

    setIsSaving(true);
    setStatusMessage('');

    const nowISO = new Date().toISOString();
    const tabItems = sections[activeTab] || [];
    const nextSections = { ...sections };

    if (isEditing && selectedItemId) {
      nextSections[activeTab] = tabItems.map((item) =>
        item.id === selectedItemId
          ? {
              ...item,
              title: draft.title.trim(),
              summary: draft.summary.trim(),
              details: draft.details.trim(),
              status: draft.status,
              priority: draft.priority,
              effectiveDate: draft.effectiveDate || '',
              updatedAt: nowISO
            }
          : item
      );
    } else {
      const newId = `${activeTab}_${Date.now()}`;
      const newItem = {
        id: newId,
        title: draft.title.trim(),
        summary: draft.summary.trim(),
        details: draft.details.trim(),
        status: draft.status,
        priority: draft.priority,
        effectiveDate: draft.effectiveDate || '',
        createdAt: nowISO,
        updatedAt: nowISO,
        createdBy: currentUser?.uid || 'demo-user'
      };
      nextSections[activeTab] = [newItem, ...tabItems];
      setSelectedItemId(newId);
      setIsEditing(true);
    }

    const success = await persistSections(nextSections);
    if (success) {
      setSections(nextSections);
      setStatusMessage(isEditing ? 'Item updated.' : 'Item created.');
    }
    setIsSaving(false);
  };

  const onDeleteItem = async () => {
    if (!selectedItemId) return;
    setIsSaving(true);
    setStatusMessage('');

    const nextSections = {
      ...sections,
      [activeTab]: (sections[activeTab] || []).filter((item) => item.id !== selectedItemId)
    };

    const success = await persistSections(nextSections);
    if (success) {
      setSections(nextSections);
      setSelectedItemId('');
      setIsEditing(false);
      setDraft(EMPTY_DRAFT);
      setStatusMessage('Item deleted.');
    }
    setIsSaving(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Business Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage entities and operational foundations for the selected business.
            </p>
            {activeBusiness?.name && (
              <p className="text-sm text-[#190192] mt-2">
                Active business: <span className="font-medium">{activeBusiness.name}</span>
              </p>
            )}
          </div>

          {isMainPage && (
            <button
              onClick={() => navigate('/business/new')}
              className="px-4 py-2 rounded-lg bg-[#190192] text-white hover:bg-[#2C0DB5] inline-flex items-center text-sm"
            >
              <PlusCircle size={16} className="mr-2" />
              Add Business
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-200 px-4 pt-4">
          <div className="flex flex-wrap gap-2">
            {FOUNDATION_TABS.map((tab) => {
              const count = sections[tab.key]?.length || 0;
              const active = tab.key === activeTab;
              return (
                <button
                  key={tab.key}
                  onClick={() => onSelectTab(tab.key)}
                  className={`px-3 py-2 rounded-t-lg text-sm border-b-2 ${
                    active
                      ? 'border-[#190192] text-[#190192] font-medium'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-5">
          {statusMessage && (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              {statusMessage}
            </div>
          )}

          {!activeBusiness?.id ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 inline-flex items-center">
              <Building2 size={16} className="mr-2" />
              No active business selected. Choose one from the Business list first.
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
              <div className="xl:col-span-1 border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900 capitalize">{activeTab}</h3>
                  <button
                    onClick={onCreateNew}
                    className="text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 inline-flex items-center"
                  >
                    <PlusCircle size={14} className="mr-1.5" />
                    New
                  </button>
                </div>

                {currentTabItems.length === 0 ? (
                  <p className="text-sm text-gray-500">No items yet.</p>
                ) : (
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                    {currentTabItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setSelectedItemId(item.id);
                          setIsEditing(false);
                        }}
                        className={`w-full text-left p-3 rounded-lg border ${
                          selectedItemId === item.id ? 'border-[#190192] bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <p className="font-medium text-sm text-gray-900">{item.title}</p>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.summary || 'No summary'}</p>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">{item.status || 'draft'}</span>
                          <span className="text-[11px] text-gray-500">{formatDate(item.updatedAt)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="xl:col-span-2 border border-gray-200 rounded-lg p-4 space-y-4">
                {selectedItem && !isEditing ? (
                  <>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{selectedItem.title}</h3>
                        <p className="text-sm text-gray-500 mt-1">{selectedItem.summary || 'No summary'}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => onEditItem(selectedItem)}
                          className="px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 inline-flex items-center"
                        >
                          <Pencil size={14} className="mr-1.5" />
                          Edit
                        </button>
                        <button
                          onClick={onDeleteItem}
                          disabled={isSaving}
                          className="px-3 py-1.5 rounded-md border border-red-200 text-red-700 text-sm hover:bg-red-50 inline-flex items-center"
                        >
                          <Trash2 size={14} className="mr-1.5" />
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="bg-gray-50 rounded-md p-2">
                        <p className="text-gray-500">Status</p>
                        <p className="text-gray-900 font-medium">{selectedItem.status || 'draft'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-md p-2">
                        <p className="text-gray-500">Priority</p>
                        <p className="text-gray-900 font-medium capitalize">{selectedItem.priority || 'medium'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-md p-2">
                        <p className="text-gray-500">Effective Date</p>
                        <p className="text-gray-900 font-medium">{selectedItem.effectiveDate || 'Not set'}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Details</p>
                      <div className="border border-gray-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap min-h-[180px]">
                        {selectedItem.details || 'No details provided.'}
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 flex items-center">
                      <Calendar size={13} className="mr-1.5" />
                      Updated: {formatDate(selectedItem.updatedAt)}
                    </div>
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {isEditing ? `Edit ${activeTab.slice(0, -1)}` : `New ${activeTab.slice(0, -1)}`}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input
                          value={draft.title}
                          onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
                          placeholder={`Enter ${activeTab.slice(0, -1)} title`}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Summary</label>
                        <input
                          value={draft.summary}
                          onChange={(event) => setDraft((prev) => ({ ...prev, summary: event.target.value }))}
                          placeholder="Short summary"
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                          <select
                            value={draft.status}
                            onChange={(event) => setDraft((prev) => ({ ...prev, status: event.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          >
                            <option value="draft">Draft</option>
                            <option value="active">Active</option>
                            <option value="review">In Review</option>
                            <option value="archived">Archived</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                          <select
                            value={draft.priority}
                            onChange={(event) => setDraft((prev) => ({ ...prev, priority: event.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Effective Date</label>
                          <input
                            type="date"
                            value={draft.effectiveDate}
                            onChange={(event) => setDraft((prev) => ({ ...prev, effectiveDate: event.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                        <textarea
                          rows={8}
                          value={draft.details}
                          onChange={(event) => setDraft((prev) => ({ ...prev, details: event.target.value }))}
                          placeholder={`Describe the ${activeTab} content in detail.`}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#190192] focus:border-[#190192]"
                        />
                      </div>
                    </div>

                    <div className="pt-2 flex items-center gap-2">
                      <button
                        onClick={onSaveItem}
                        disabled={isSaving}
                        className={`px-4 py-2 rounded-lg text-sm inline-flex items-center ${
                          isSaving ? 'bg-gray-300 text-gray-700 cursor-not-allowed' : 'bg-[#190192] text-white hover:bg-[#2C0DB5]'
                        }`}
                      >
                        <Save size={15} className="mr-1.5" />
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setDraft(EMPTY_DRAFT);
                        }}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Outlet />
    </div>
  );
};

export default BusinessPage;
